/**
 * SN_SAI_POL_REG_001 — 포인트 정책 (본사) (2026-09-18, 2차 검토 확정)
 *
 * 왼쪽 거래처(잔액 · 이달 사용) — 보기만. 오른쪽 그 거래처의 정책(crm_policy_ai) — 기간별 줄로 쌓인다. 덮어쓰지 않는다.
 * 한 줄 = "이 거래처는 이 기간 동안 이 방식으로 깎는다" : 시작일 · 종료일 · 과금방식(선불 · 후불 · 무료) · 차감단위 · 월상한 · 지정등급 · 지정LLM · 지정모델.
 * 종료일 빈칸 → 2100-12-31. 기간이 겹치면 서버가 거부한다 — 앞 줄의 종료일을 먼저 닫는다. 순번은 서버가 채번한다.
 * 테스트 기간은 무료 줄로 표현한다 — 환경(테스트 · 운영) 개념은 없다.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_pol_reg_001_01.
 * 본 : 코드관리 SN_MAS_SET_REG_001 (좌 · 우 그리드)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_POL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
          grid2: rtn.grid.grid2 || [],
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid2"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      gridRefs.current["grid2"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_pol_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
        grid2: data?.[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const focused = gridRefs.current["grid1"].current?.focusedRowNode();
      if (!focused) {
        await showDialog("alter", "왼쪽에서 거래처를 먼저 선택해 주세요.");
        return;
      }
      gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
    const confirm = await showDialog("confirm", "선택한 행을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid2"].current.stopEditing();
      if (!gridRefs.current["grid2"].current.requiredValuesCheck()) return false;
      const crm_policy_aiChanges = gridRefs.current["grid2"].current.getChange();
      if (crm_policy_aiChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        crm_policy_ai: crm_policy_aiChanges,
      };
      const ok = await fnSave("SN_SAI_POL_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid2"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 오른쪽 새 행의 기본값 — 왼쪽 거래처, 오늘 ~ 2100-12-31, 선불 · 토큰비례. 순번은 서버가 채번 */
  const onBeforeAddPolicy = (): any => {
    const focused = gridRefs.current["grid1"].current?.focusedRowNode();
    if (!focused) return false;
    /* 종료일 21001231 · 과금방식 PRE · 차감단위 RATE 는 sys_formgridcolumn.cd_value 가 넣는다 */
    return { cd_firm: userInfo.cd_firm, cd_cust: focused.data?.cd_cust, dt_from: dateToString(new Date()) };
  };
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddPolicy
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panelCust" orgSize="40%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "거래처", isEditable: false }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["cd_cust"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panelPolicy" orgSize="59%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "정책" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SAI_POL_REG_001;
